import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import { setProfileAction } from "../../store/AthleteActions";
import { setScoreConfig } from "../../store/slices/athleteSlice";
import AthleteInformation from "./athlete-information";
import CombineResults from "./combines/athlete-combine-results";
import LoadingSpinner from "../LoadingSpinner";
import AthleteMeasurements from "./measurements/athlete-measurements";
import AthleteCombineButtons from "./combines/athlete-combine-buttons";
import PositionDrills from "./position-drills/positionDrills";
import AthleteAdditionalInfo from "./additional-info/athleteAdditionalInfo";
import { useResponsiveHeight } from "../../hooks/useResponsiveHeight";
import { getAthleteProfile } from "../../services/AthleteService";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

const SCORE_INFO_TITLE = "Score Info";
const SCORE_INFO_DESCRIPTIONS = {
  overall:
    "All of the scores on this page represent the percentile compared to other athletes who have attended any Trackit Verified Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.",
  combine:
    "All of the scores on this page represent the percentile compared to other athletes who have attended the same Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.",
};

function getScoreInfoDescription({ isCombineScoresView }) {
  return isCombineScoresView
    ? SCORE_INFO_DESCRIPTIONS.combine
    : SCORE_INFO_DESCRIPTIONS.overall;
}

export default function AthleteScore({
  userId,
  showOffers,
  showTrackittScore,
  showPositionScore,
  searchEvent,
  showProfile,
  showRank,
  showLikes,
  sharedProfile = false,
  showCompare = true,
  eventId = undefined,
  onFavoritesRefresh,
  isolateProfile = false,
}) {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.athletes.profile, shallowEqual);
  const loading = useSelector((state) => state.athletes.loading);
  const buttons = useSelector((state) => state.athletes.buttons);
  const authUser = useAuthUser();
  const apiInstance = useAxiosInstance();
  const [move, setMove] = useState(0);
  const [tab, setTab] = useState(1);
  const [showScoreInfoModal, setShowScoreInfoModal] = useState(false);
  const hasSubscription =
    useSelector((state) => state.user.hasSubscription) ||
    authUser.user.role === "highschool";
  const subscriptionChecked = useSelector(
    (state) => state.user.subscriptionChecked,
  );

  const isLocked =
    sharedProfile && !hasSubscription && authUser.user.role !== "highschool";
  // Must mirror AthleteInformation's showSubscribeNow: this only reserves the
  // banner's height, so the two conditions have to resolve together.
  const showSubscribeNow =
    subscriptionChecked && !sharedProfile && !hasSubscription;
  const { adjustedHeight: rawAdjustedHeight, isMobile } = useResponsiveHeight({
    breakpoint: 640,
  });
  const adjustedHeight = Math.max(
    0,
    showSubscribeNow ? rawAdjustedHeight - 100 : rawAdjustedHeight,
  );
  const [localProfile, setLocalProfile] = useState(null);
  const isCombineScoresView = eventId !== undefined && eventId !== null;
  const scoreInfoDescription = getScoreInfoDescription({
    isCombineScoresView,
  });
  const style = {
    ul: "flex space-x-0 mt-0 border-b border-gray-300",
    li: "text-gray-900 pb-2 px-3 xl:px-4 lg:px-4 hover:opacity-70 text-sm xl:text-base cursor-pointer whitespace-nowrap",
    selected:
      "cursor-default border-b-4 border-blue-700 text-sm xl:text-base text-blue-700 pb-2 px-3 xl:px-4 lg:px-4 font-medium whitespace-nowrap ",
  };

  useEffect(() => {
    async function getProfileActive() {
      // popup uses its own profile
      // dispatch(setLoading(true));
      const response = await getAthleteProfile(apiInstance, userId);
      setLocalProfile(response);
      //dispatch(setLoading(false));
    }
    if (isolateProfile) {
      getProfileActive();
    } else {
      dispatch(setProfileAction(apiInstance, userId));
    }
    dispatch(
      setScoreConfig({
        showOffers: showOffers,
        showTrackittScore: showTrackittScore,
        showPositionScore: showPositionScore,
        searchEvent: searchEvent,
        eventId: eventId,
        showProfile: showProfile,
        showRank: showRank,
        showLikes: showLikes,
        sharedProfile: sharedProfile,
        showCompare: showCompare,
      }),
    );
    setMove(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, apiInstance, userId]);
  const activeProfile = isolateProfile ? localProfile : profile;

  if (!activeProfile || loading)
    return (
      <div className="my-10 lg:w-full xl:h-full">
        <LoadingSpinner />
      </div>
    );

  return (
    move === 1 &&
    activeProfile &&
    !loading && (
      <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300 relative md:h-auto md:overflow-visible">
        <div
          className={`top-0 bg-white z-20 ${isMobile === false ? "sticky" : ""}`}
        >
          <AthleteInformation
            sharedProfile={sharedProfile}
            showCompare={showCompare}
            profile={activeProfile}
            onFavoritesRefresh={onFavoritesRefresh}
          />
          <ul className={style.ul}>
            {!isLocked && (
              <li
                className={tab === 1 ? style.selected : style.li}
                onClick={() => setTab(1)}
              >
                Combine Results
              </li>
            )}
            {!isLocked && (
              <li
                className={tab === 2 ? style.selected : style.li}
                onClick={() => setTab(2)}
              >
                Position Drills
              </li>
            )}
            {!isLocked && (
              <li
                className={tab === 3 ? style.selected : style.li}
                onClick={() => setTab(3)}
              >
                Additional Info
              </li>
            )}
          </ul>
          {tab === 1 && !isLocked && buttons.combine.tab && (
            <AthleteCombineButtons profile={activeProfile} />
          )}
        </div>
        {tab === 1 && !isLocked && (
          <div
            className="mt-0 px-4 -m-4 pb-12 md:overflow-y-auto"
            style={
              isMobile === false ? { height: `${adjustedHeight}px` } : undefined
            }
          >
            <div className="pt-0 mb-10">
              <div className="flex justify-between items-center">
                <h1 className="text-xl font-medium">Measurements </h1>
                <button
                  type="button"
                  className="flex items-center bg-white border hover:bg-gray-200 border-gray-300 rounded-md text-sm px-3 py-1.5 cursor-pointer transition"
                  onClick={() => setShowScoreInfoModal(true)}
                >
                  Score Info
                  <span className="pl-2">
                    <svg
                      fill="#000000"
                      width="18px"
                      height="18px"
                      viewBox="-2 0 19 19"
                      xmlns="http://www.w3.org/2000/svg"
                      className="cf-icon-svg"
                    >
                      <path d="M13.55 15.256H1.45a.554.554 0 0 1-.553-.554V3.168a.554.554 0 1 1 1.108 0v10.98h11.544a.554.554 0 0 1 0 1.108zM3.121 13.02V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.785 0V3.507a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v9.513zm2.785 0V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.786 0v-2.753a.476.476 0 0 1 .475-.475h.785a.476.476 0 0 1 .475.475v2.753z" />
                    </svg>
                  </span>
                </button>
              </div>

              <AthleteMeasurements profile={activeProfile} />
              <h1 className="text-xl font-medium mt-6">Athletic Score</h1>
              <CombineResults profile={activeProfile} />
            </div>
          </div>
        )}
        {tab === 2 && !isLocked && (
          <div
            className="mt-0 px-4 -m-4 pb-12 md:overflow-y-auto"
            style={
              isMobile === false
                ? { height: `${adjustedHeight + 80}px` }
                : undefined
            }
          >
            {" "}
            <PositionDrills userId={userId} />
          </div>
        )}
        {tab === 3 && !isLocked && (
          <div
            className="mt-0 px-4 -m-4 pb-12 md:overflow-y-auto"
            style={
              isMobile === false
                ? { height: `${adjustedHeight + 80}px` }
                : undefined
            }
          >
            <AthleteAdditionalInfo userId={userId} />
          </div>
        )}
        {showScoreInfoModal && (
          <Modal
            title={SCORE_INFO_TITLE}
            description={scoreInfoDescription}
            onClose={() => setShowScoreInfoModal(false)}
          />
        )}
      </div>
    )
  );
}

function Modal({ title, description, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-3/4 max-w-md">
        <h2 className="text-lg font-bold mb-4">{title}</h2>
        <p className="text-sm mb-6">{description}</p>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
